import { getSibling, addClass, removeClass } from './DOMHelpers';

export function copyObject(options) {
    return JSON.parse(JSON.stringify(options));
}

export function getOptions(element: HTMLElement, defOptions: any = {}): any {
    let optionsStringFromData = element.dataset.options,
        optionsFromData;

    if (optionsStringFromData) {
        try {
            optionsFromData = JSON.parse(optionsStringFromData);
        } catch (e) {
            console.warn(`Bad data-options for:/\n${element}`);
        }
    }

    return typeof optionsFromData === 'object'
        ? Object.assign(defOptions, optionsFromData)
        : defOptions;
}

// Filter helper functions
export function toggleSelection(input: HTMLInputElement, select: boolean = true): void {
    input.checked = select;
}

export function getNamesOfInputs(inputs: FilterCheckBox[]): string[] {
    return inputs.sort(compareTimeStamp).map(getFilterName);
}

export function compareTimeStamp(a: FilterCheckBox, b: FilterCheckBox): number {
    return a.timeStamp > b.timeStamp ? 1 : -1;
}

export function getFilterName(element: HTMLElement): string {
    const node = element.nextElementSibling;
    return node ? node.textContent.trim() : '';
}

export function getCateforyName(nameNodeSelector: string, element: HTMLElement): string {
    const nameNode = getSibling(element, nameNodeSelector);
    return nameNode ? nameNode.textContent : '';
}

export function getPageScrollTop(): number {
    return document.documentElement.scrollTop || document.body.scrollTop;
}

export function setPageScrollTop(value: number): void {
    document.documentElement.scrollTop = document.body.scrollTop = value;
}

// horizontalScrolling
export function horizontalScrolling(element: HTMLElement) {
    let x: number,
        left: number,
        down: boolean;

    function onMouseDown(event: MouseEvent): void {
        event.preventDefault();
        down = true;
        x = event.pageX;
        left = element.scrollLeft;

        document.body.addEventListener('mousemove', onBodyMouseMove);
        document.body.addEventListener('mouseup', onBodyMouseUp);
    }

    function onBodyMouseMove(event: MouseEvent): void {
        if (down) {
            const newX = event.pageX;
            element.scrollTo({ left: left - newX + x });
        }
    }

    function onBodyMouseUp(): void {
        document.body.removeEventListener('mousemove', onBodyMouseMove);
        document.body.removeEventListener('mouseup', onBodyMouseUp);
        down = false;
    }

    element.addEventListener('mousedown', onMouseDown);
}

// Cookie methods
export function setCookie(cname: string, cvalue: string, exdays: number) {
    var d = new Date();
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
    var expires = 'expires=' + d.toUTCString();
    document.cookie = cname + '=' + cvalue + '; ' + expires + ";path=/";
}

export function deleteCookie(name: string) {
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

export function getCookie(cname: string): string {
    var name = cname + '=',
        ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) !== -1) {
            return c.substring(name.length, c.length);
        }
    }
    return '';
}

// IOS prevent scrolling
export class IOSScroll {
    static prevent(visual: boolean = false, preventClass: string = 'nx-u-prevent-scroll'): void {
        if (!isIOS()) {
            document.body['preventLevel'] = document.body['preventLevel'] + 1 || 1;
            addClass(document.body, 'nx-u-lock-screen');
            return;
        }

        const scrollTop = getPageScrollTop();

        IOSScroll.scrollTop.unshift(IOSScroll.scrollTop.length ? IOSScroll.scrollTop[0] : scrollTop);
        document.body.classList.add(preventClass);
        setTimeout(() =>
            IOSScroll.setNegativeMarginTop(
                visual ? IOSScroll.scrollTop[0] : null
            ), IOSScroll.scrollTop.length > 1 ? 500 : 0);
    }

    static allow(visual: boolean = false, preventClass: string = 'nx-u-prevent-scroll'): void {
        if (!isIOS()) {
            document.body['preventLevel'] = Math.max(document.body['preventLevel'] - 1, 0);
            document.body['preventLevel'] === 0 && removeClass(document.body, 'nx-u-lock-screen');
            return;
        }

        const firstScrollTop = IOSScroll.scrollTop.shift();
        IOSScroll.setNegativeMarginTop(IOSScroll.scrollTop[0] || null);

        if (IOSScroll.scrollTop.length === 0) {
            document.body.classList.remove(preventClass);
            setPageScrollTop(firstScrollTop);
        }
    }

    private static setNegativeMarginTop(value: number): void {
        document.body.style.marginTop = value && `-${value}px`;
    }

    static scrollTop: number[] = [];
}

export function isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window['MSStream'];
}

export function isIE11(): boolean {
    return !!navigator.userAgent.match(/Trident.*rv\:11\./);
}

export function focusInput(input: HTMLInputElement): HTMLInputElement {
    let position = input.value.length;

    if (isIE11() && position) {
        input.setSelectionRange(position, position);
    }

    input.focus();

    return input;
}

export function parseJSONSafe(json: string): any {
    let result = null;

    if (json) {
        try {
            result = JSON.parse(json);
        } catch (e) {
            console.warn(e.message);
        }
    }

    return result;
}

export function cleanString(str: string): string {
    return str.replace(/[^\w\W\s\u00C0-\u00FF]/gi, '');
}

export function removeFromArray(arr: any[], element: any) {
    arr.splice(arr.indexOf(element), 1);
};

export function requestNextAnimationFrame(func: () => any): number {
    return requestAnimationFrame(requestAnimationFrame.bind(null, func));
}

export function requestAnimationFrameTimer(func: () => any, time: number = 0): void {
    requestAnimationFrame(
        () => setTimeout(
            () => func(),
            time)
    );
}

export function limitCall(func, delay: number) {
    let timerId;

    return function (...args) {
        if (timerId) return;
        timerId = setTimeout(() => timerId = null, delay);
        func.apply(this, args);
    }
}

export function getLastItemInArray(arr: any[]): any {
    const length = arr.length;
    return length > 0 ? arr[arr.length - 1] : arr[0];
}

export function getURLParamsArray(): string[] {
    return window.location.search.substring(1).split('&');
}